/**
 * /tools/virtue-shortlist?clientId=X — F-028 (2026-05-24).
 *
 * Step 2 placeholder tool — the client picks their top 3-5 virtues from a
 * curated catalog of ~50 (with write-in fallback), assigns each to one or
 * more axes, and writes a 1-line reason. Feeds the Carpe Diem Pool as
 * high-confidence virtue proposals. Replaceable.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { ApiError } from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';

type Axis = 'energy' | 'work' | 'love';

interface CuratedVirtue {
  name: string;
  defaultAxes: Axis[];
}

/**
 * Same set as apps/api/src/tools/schemas/virtue-shortlist.ts. Kept inline +
 * canonical here (replaceable as a unit when the tool evolves). F-038
 * (2026-05-26): cross axis retired — each virtue lives on one of E/W/L.
 */
const CURATED_VIRTUES: CuratedVirtue[] = [
  { name: 'Vitality', defaultAxes: ['energy'] },
  { name: 'Tranquility', defaultAxes: ['energy'] },
  { name: 'Presence', defaultAxes: ['energy', 'love'] },
  { name: 'Joy', defaultAxes: ['energy', 'love'] },
  { name: 'Patience', defaultAxes: ['energy'] },
  { name: 'Gratitude', defaultAxes: ['energy', 'love'] },
  { name: 'Stillness', defaultAxes: ['energy'] },
  { name: 'Playfulness', defaultAxes: ['energy', 'love'] },
  { name: 'Curiosity', defaultAxes: ['energy', 'work'] },
  { name: 'Openness', defaultAxes: ['energy', 'love'] },
  { name: 'Discipline', defaultAxes: ['work'] },
  { name: 'Focus', defaultAxes: ['work'] },
  { name: 'Mastery', defaultAxes: ['work'] },
  { name: 'Excellence', defaultAxes: ['work'] },
  { name: 'Craft', defaultAxes: ['work'] },
  { name: 'Persistence', defaultAxes: ['work'] },
  { name: 'Courage', defaultAxes: ['work'] },
  { name: 'Resourcefulness', defaultAxes: ['work'] },
  { name: 'Integrity', defaultAxes: ['work', 'love'] },
  { name: 'Ambition', defaultAxes: ['work'] },
  { name: 'Creativity', defaultAxes: ['work'] },
  { name: 'Decisiveness', defaultAxes: ['work'] },
  { name: 'Devotion', defaultAxes: ['love'] },
  { name: 'Empathy', defaultAxes: ['love'] },
  { name: 'Kindness', defaultAxes: ['love'] },
  { name: 'Loyalty', defaultAxes: ['love'] },
  { name: 'Generosity', defaultAxes: ['love'] },
  { name: 'Compassion', defaultAxes: ['love'] },
  { name: 'Affection', defaultAxes: ['love'] },
  { name: 'Tenderness', defaultAxes: ['love'] },
  { name: 'Forgiveness', defaultAxes: ['love'] },
  { name: 'Listening', defaultAxes: ['love'] },
  { name: 'Vulnerability', defaultAxes: ['love'] },
  { name: 'Honesty', defaultAxes: ['love'] },
  { name: 'Humility', defaultAxes: ['love'] },
  { name: 'Wisdom', defaultAxes: ['work'] },
  { name: 'Self-respect', defaultAxes: ['energy'] },
  { name: 'Responsibility', defaultAxes: ['work'] },
  { name: 'Acceptance', defaultAxes: ['love'] },
  { name: 'Authenticity', defaultAxes: ['love'] },
  { name: 'Faith', defaultAxes: ['energy'] },
  { name: 'Hope', defaultAxes: ['energy'] },
  { name: 'Strength', defaultAxes: ['energy'] },
  { name: 'Resilience', defaultAxes: ['energy'] },
  { name: 'Grace', defaultAxes: ['love'] },
  { name: 'Service', defaultAxes: ['love'] },
  { name: 'Gentleness', defaultAxes: ['love'] },
  { name: 'Trust', defaultAxes: ['love'] },
  { name: 'Boldness', defaultAxes: ['work'] },
  { name: 'Equanimity', defaultAxes: ['energy'] },
  { name: 'Order', defaultAxes: ['work'] },
  { name: 'Wonder', defaultAxes: ['energy'] },
];

interface Pick {
  virtue: string;
  axes: Axis[];
  reason: string;
}

interface FormState {
  picks: Pick[];
  reflection: string;
}

const DEFAULT_FORM: FormState = { picks: [], reflection: '' };
const ALL_AXES: Axis[] = ['energy', 'work', 'love'];

export function VirtueShortlistPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [customInput, setCustomInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // F-007: the Save button disables once the saved picks are unchanged.
  const { isPristine, markSaved } = usePristine<FormState>(DEFAULT_FORM);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'virtue-shortlist');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const picks: Pick[] = Array.isArray(p.picks)
            ? (p.picks as Record<string, unknown>[]).map((pp) => ({
                virtue: typeof pp.virtue === 'string' ? pp.virtue : '',
                axes: Array.isArray(pp.axes) ? (pp.axes as Axis[]) : [],
                reason: typeof pp.reason === 'string' ? pp.reason : '',
              })).filter((pp) => pp.virtue.length > 0)
            : [];
          const hydratedForm: FormState = {
            picks: picks.slice(0, 5),
            reflection: typeof p.reflection === 'string' ? p.reflection : '',
          };
          setForm(hydratedForm);
          markSaved(hydratedForm);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canAddMore = form.picks.length < 5;
  const canSubmit = !submitting && hydrated && form.picks.length >= 1 && !isPristine(form);

  const addPick = (virtue: string, axes: Axis[]): void => {
    if (!canAddMore || virtue.trim().length === 0) return;
    if (form.picks.some((p) => p.virtue.toLowerCase() === virtue.toLowerCase())) return;
    setForm((prev) => ({
      ...prev,
      picks: [...prev.picks, { virtue: virtue.trim(), axes, reason: '' }],
    }));
  };

  const removePick = (idx: number): void => {
    setForm((prev) => ({ ...prev, picks: prev.picks.filter((_, i) => i !== idx) }));
  };

  const toggleAxis = (idx: number, axis: Axis): void => {
    setForm((prev) => {
      const picks = [...prev.picks];
      const cur = picks[idx];
      if (!cur) return prev;
      const has = cur.axes.includes(axis);
      picks[idx] = {
        ...cur,
        axes: has ? cur.axes.filter((a) => a !== axis) : [...cur.axes, axis],
      };
      return { ...prev, picks };
    });
  };

  const setReason = (idx: number, reason: string): void => {
    setForm((prev) => {
      const picks = [...prev.picks];
      const cur = picks[idx];
      if (!cur) return prev;
      picks[idx] = { ...cur, reason };
      return { ...prev, picks };
    });
  };

  const movePick = (idx: number, dir: -1 | 1): void => {
    setForm((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.picks.length) return prev;
      const picks = [...prev.picks];
      const a = picks[idx]!;
      const b = picks[j]!;
      picks[idx] = b;
      picks[j] = a;
      return { ...prev, picks };
    });
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.virtueShortlist.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        picks: form.picks.map((p) => ({
          virtue: p.virtue,
          axes: p.axes,
          ...(p.reason.trim() ? { reason: p.reason.trim() } : {}),
        })),
        ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
      };
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'virtue-shortlist',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      markSaved(form);
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.virtueShortlist.errors.generic'));
      } else {
        setSubmitError(t('tools.virtueShortlist.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-3xl mx-auto px-s5 py-s8" data-testid="virtue-shortlist-missing-client">
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.virtueShortlist.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.virtueShortlist.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.virtueShortlist.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.virtueShortlist.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.virtueShortlist.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {/* Picks list */}
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <div className="flex items-center justify-between mb-s3">
            <span className="form-label">
              {t('tools.virtueShortlist.picks.label', { count: form.picks.length })}
            </span>
            <span className="text-xs text-gray-1">
              {t('tools.virtueShortlist.picks.limit', { current: form.picks.length, max: 5 })}
            </span>
          </div>
          {form.picks.length === 0 && (
            <p className="text-sm text-gray-1 italic">{t('tools.virtueShortlist.picks.empty')}</p>
          )}
          <ol className="space-y-s3">
            {form.picks.map((pick, idx) => (
              <li key={`${pick.virtue}-${idx}`} className="rounded-md border border-bone-3 bg-bone-1 p-s3">
                <div className="flex items-center gap-s2">
                  <span className="font-display text-lg text-flame-deep w-6 text-center">
                    {idx + 1}
                  </span>
                  <span className="font-semibold text-ink flex-1">{pick.virtue}</span>
                  <div className="flex gap-s1">
                    <button
                      type="button"
                      onClick={() => movePick(idx, -1)}
                      disabled={idx === 0}
                      className="text-gray-1 hover:text-ink disabled:opacity-30"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => movePick(idx, 1)}
                      disabled={idx === form.picks.length - 1}
                      className="text-gray-1 hover:text-ink disabled:opacity-30"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removePick(idx)}
                      className="text-gray-1 hover:text-flame-ember"
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="mt-s2 flex flex-wrap gap-s1">
                  {ALL_AXES.map((axis) => {
                    const on = pick.axes.includes(axis);
                    return (
                      <button
                        key={axis}
                        type="button"
                        onClick={() => toggleAxis(idx, axis)}
                        className={`rounded-md px-s2 py-1 text-xs font-medium ${
                          on
                            ? 'bg-flame-glow text-flame-deep'
                            : 'bg-bone-2 text-gray-2 hover:bg-bone-3'
                        }`}
                      >
                        {t(`tools.virtueShortlist.axis.${axis}`)}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  value={pick.reason}
                  onChange={(e) => setReason(idx, e.target.value)}
                  maxLength={280}
                  placeholder={t('tools.virtueShortlist.reasonPlaceholder')}
                  className="input-field mt-s2 text-sm"
                  data-testid={`virtue-shortlist-reason-${idx}`}
                />
              </li>
            ))}
          </ol>
        </article>

        {/* Curated catalog */}
        {canAddMore && (
          <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <p className="form-label">{t('tools.virtueShortlist.catalog.label')}</p>
            <p className="text-xs text-gray-1 mb-s3">{t('tools.virtueShortlist.catalog.hint')}</p>
            <div className="flex flex-wrap gap-s2">
              {CURATED_VIRTUES.map((virtue) => {
                const picked = form.picks.some(
                  (p) => p.virtue.toLowerCase() === virtue.name.toLowerCase(),
                );
                return (
                  <button
                    key={virtue.name}
                    type="button"
                    onClick={() => addPick(virtue.name, virtue.defaultAxes)}
                    disabled={picked}
                    className={`rounded-full px-s3 py-s1 text-sm border ${
                      picked
                        ? 'border-flame-glow bg-flame-glow text-flame-deep opacity-50'
                        : 'border-bone-3 bg-bone-1 text-ink hover:border-flame-ember hover:text-flame-deep'
                    }`}
                  >
                    {picked ? '✓ ' : '+ '}
                    {virtue.name}
                  </button>
                );
              })}
            </div>

            {/* Write-in */}
            <div className="mt-s4 pt-s4 border-t border-bone-3">
              <p className="form-label">{t('tools.virtueShortlist.writeIn.label')}</p>
              <p className="text-xs text-gray-1 mb-s2">{t('tools.virtueShortlist.writeIn.hint')}</p>
              <div className="flex gap-s2">
                <input
                  type="text"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  maxLength={80}
                  placeholder={t('tools.virtueShortlist.writeIn.placeholder')}
                  className="input-field flex-1"
                  data-testid="virtue-shortlist-write-in"
                />
                <button
                  type="button"
                  onClick={() => {
                    // F-038: cross axis retired. Default write-ins to 'energy';
                    // the client can re-tag via the axis chips below.
                    addPick(customInput.trim(), ['energy']);
                    setCustomInput('');
                  }}
                  disabled={customInput.trim().length === 0}
                  className="btn-secondary"
                >
                  {t('tools.virtueShortlist.writeIn.add')}
                </button>
              </div>
            </div>
          </article>
        )}

        {/* Reflection */}
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.virtueShortlist.reflection.label')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.virtueShortlist.optional')}
              </span>
            </span>
            <p className="text-xs text-gray-1 mb-s2">
              {t('tools.virtueShortlist.reflection.hint')}
            </p>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-20"
              data-testid="virtue-shortlist-reflection"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="virtue-shortlist-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="virtue-shortlist-saved-at"
          >
            ✓ {t('tools.virtueShortlist.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting
            ? t('tools.virtueShortlist.submitting')
            : t('tools.virtueShortlist.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
